import type { IServices } from "../CustomHooks/useMarketBuilderServices";
import { formatDomainText, getValidDomains } from "./DomainsDataHandler";
import type { IDomain } from "../MarketBuilder.types";
import { allTrackers } from "app/services/track/track";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
export const MAX_DOMAINS_ALLOWED = MAX_DOMAINS_IN_CATEGORY; // we might in the future want to use different amount on market builder

const allowedFileTypes = {
    ext: [".csv", ".txt"],
    mime: ["text/csv", "text/plain"],
};

interface IImportDomainsResult {
    isSuccess: boolean;
    errorMessage?: string;
    file?: File;
    domains?: IDomain[];
}

export const getAllowedFileTypesString = () => {
    return [...allowedFileTypes.ext.map((ext) => `.${ext}`), ...allowedFileTypes.mime].join(",");
};

const EVENT_CATEGORY = "upload";
const EVENT_ACTION = "message";

export const importFileContent = async (
    files: File[],
    services: IServices,
    selectedDomains: IDomain[],
): Promise<IImportDomainsResult> => {
    if (files.length > 1) {
        return {
            isSuccess: false,
            errorMessage: services.translate("marketbuilder.importModal.error.multipleFiles"),
        };
    }
    const fileToImport = files[0];
    const isFileTypeValid = isFileTypeSupported(fileToImport);
    if (!isFileTypeValid) {
        return {
            isSuccess: false,
            errorMessage: services.translate("marketbuilder.importModal.error.invalidFileType"),
        };
    }

    const fileContent = await readFileContent(fileToImport);
    if (!fileContent) {
        allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/importModal/errorMessage/invalidStructure`);
        return {
            isSuccess: false,
            errorMessage: services.translate("marketbuilder.importModal.error.invalidStructure"),
        };
    }

    const parsedContent = parseFileContent(fileContent);
    if (MAX_DOMAINS_ALLOWED - selectedDomains.length < parsedContent.length) {
        allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/importModal/errorMessage/PassedTheLimit`);
        return {
            isSuccess: false,
            errorMessage: services.translate("marketbuilder.importModal.error.passedTheLimit", {
                maxAmount: MAX_DOMAINS_IN_CATEGORY,
            }),
        };
    }
    if (parsedContent.length <= 0) {
        allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/importModal/errorMessage/invalidStructure`);
        return {
            isSuccess: false,
            errorMessage: services.translate("marketbuilder.importModal.error.noDomains"),
        };
    }

    return {
        isSuccess: true,
        domains: parsedContent,
        file: fileToImport,
    };
};

const parseFileContent = (content: string): IDomain[] => {
    const fileRecords = content
        .split(/\r\n|\n/)
        .filter((record) => Boolean(record))
        .map(formatDomainText)
        .filter((domain) => Boolean(domain));

    const domains = fileRecords.map((domain) => {
        return {
            domain,
            favicon: null,
            id: domain,
        };
    });

    return getValidDomains(domains);
};

const readFileContent = async (file: File): Promise<string | null> => {
    try {
        return await file.text();
    } catch (e) {
        return null;
    }
};

const isFileTypeSupported = (file: File) => {
    // check if mimetype or extension of the file is allowed
    const lastPeriodPos = file.name.lastIndexOf(".");
    const fileExt = lastPeriodPos !== -1 ? file.name.slice(lastPeriodPos) : null;
    return allowedFileTypes.mime.includes(file.type) || allowedFileTypes.ext.includes(fileExt);
};
